import { useMemo, useState } from 'react'
import PaginationControls, { paginateItems } from '../components/PaginationControls'
import { useApp } from '../context/AppContext'
import { persistDeletedVersion, persistDeletedVersions } from '../lib/lumiAppendOnly'
import { formatCurrency, formatDate, gerarCodigo } from '../utils'
import type { ContaPagar } from '../types'

const STATUS_COLOR: Record<string, string> = {
  PREVISTO: '#94a3b8',
  PROVISIONADO: '#60a5fa',
  PENDENTE: '#fbbf24',
  PAGO: '#4ade80',
  PARCIALMENTE_PAGO: '#34d399',
  ATRASADO: '#f87171',
  CANCELADO: '#6b7280',
}

const statusAbertos = ['PREVISTO', 'PROVISIONADO', 'PENDENTE', 'PARCIALMENTE_PAGO', 'ATRASADO']

function escapeCell(value: string | number) {
  return String(value ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

export default function ContasPagar() {
  const {
    unidades,
    contasPagar,
    setContasPagar,
    pagamentos,
    setPagamentos,
    itensOrdemCompra,
    setItensOrcados,
    obras,
    centrosCusto,
    categorias,
    fornecedores,
    addLog,
  } = useApp()
  const [modal, setModal] = useState(false)
  const [editId, setEditId] = useState<string | null>(null)
  const [filterStatus, setFilterStatus] = useState('')
  const [filterUnidade, setFilterUnidade] = useState('')
  const [filterObra, setFilterObra] = useState('')
  const [filterFornecedor, setFilterFornecedor] = useState('')
  const [filterCategoria, setFilterCategoria] = useState('')
  const [periodoInicio, setPeriodoInicio] = useState('')
  const [periodoFim, setPeriodoFim] = useState('')
  const [toast, setToast] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  const emptyForm = {
    ordem_compra_id: '',
    obra_id: '',
    centro_custo_id: '',
    categoria_orcamentaria_id: '',
    fornecedor_id: '',
    descricao: '',
    data_emissao: new Date().toISOString().slice(0, 10),
    data_vencimento: '',
    valor_previsto: 0,
    origem: 'LANCAMENTO_MANUAL_CONTROLADO' as ContaPagar['origem'],
    observacoes: '',
  }
  const [form, setForm] = useState({ ...emptyForm })

  const hoje = new Date().toISOString().slice(0, 10)
  const obrasFiltro = filterUnidade ? obras.filter(obra => obra.unidade_id === filterUnidade) : obras
  const ccsFiltrados = centrosCusto.filter(cc => cc.obra_id === form.obra_id)
  const obraName = (id: string) => obras.find(o => o.id === id)?.nome_obra || '-'
  const unidadeName = (obraId: string) => {
    const obra = obras.find(o => o.id === obraId)
    return unidades.find(unidade => unidade.id === obra?.unidade_id)?.nome || '-'
  }
  const fornName = (id: string) => fornecedores.find(f => f.id === id)?.nome_fantasia || '-'
  const categoriaName = (id: string) => categorias.find(c => c.id === id)?.nome || '-'
  const saldoAberto = (cp: ContaPagar) => Math.max(0, cp.valor_previsto - cp.valor_pago)

  const filtered = useMemo(() => contasPagar.filter(conta => {
    const obra = obras.find(item => item.id === conta.obra_id)
    if (filterUnidade && obra?.unidade_id !== filterUnidade) return false
    if (filterObra && conta.obra_id !== filterObra) return false
    if (filterFornecedor && conta.fornecedor_id !== filterFornecedor) return false
    if (filterCategoria && conta.categoria_orcamentaria_id !== filterCategoria) return false
    if (filterStatus && conta.status !== filterStatus) return false
    if (periodoInicio && conta.data_vencimento < periodoInicio) return false
    if (periodoFim && conta.data_vencimento > periodoFim) return false
    return true
  }), [contasPagar, obras, filterUnidade, filterObra, filterFornecedor, filterCategoria, filterStatus, periodoInicio, periodoFim])

  const totalPrevisto = filtered.reduce((s, conta) => s + conta.valor_previsto, 0)
  const totalPago = filtered.reduce((s, conta) => s + conta.valor_pago, 0)
  const paginatedContas = paginateItems(filtered, page, pageSize)
  const totalAberto = filtered
    .filter(conta => statusAbertos.includes(conta.status))
    .reduce((s, conta) => s + saldoAberto(conta), 0)
  const totalVencido = filtered
    .filter(conta => conta.data_vencimento < hoje && !['PAGO', 'CANCELADO'].includes(conta.status))
    .reduce((s, conta) => s + saldoAberto(conta), 0)
  const qtdPagas = filtered.filter(conta => conta.status === 'PAGO').length
  const qtdAbertas = filtered.filter(conta => statusAbertos.includes(conta.status)).length

  function showToast(msg: string) {
    setToast(msg)
    setTimeout(() => setToast(''), 3000)
  }

  function validate() {
    const e: Record<string, string> = {}
    if (!form.obra_id) e.obra_id = 'Obra obrigatoria'
    if (!form.fornecedor_id) e.fornecedor_id = 'Fornecedor obrigatorio'
    if (!form.descricao.trim()) e.descricao = 'Descricao obrigatoria'
    if (!form.data_vencimento) e.data_vencimento = 'Vencimento obrigatorio'
    if (!form.valor_previsto || form.valor_previsto <= 0) e.valor_previsto = 'Valor obrigatorio'
    return e
  }

  function save() {
    const e = validate()
    if (Object.keys(e).length) {
      setErrors(e)
      return
    }
    if (editId) {
      const atual = contasPagar.find(conta => conta.id === editId)
      if (!atual) return
      const atualizada: ContaPagar = {
        ...atual,
        ...form,
        status: atual.valor_pago >= form.valor_previsto - 0.01 ? 'PAGO' : atual.valor_pago > 0 ? 'PARCIALMENTE_PAGO' : atual.status,
      }
      setContasPagar(prev => prev.map(conta => conta.id === editId ? atualizada : conta))
      addLog('CONTA_PAGAR', editId, 'EDITADO', `Conta ${atual.numero_titulo} atualizada`)
      showToast('Conta a pagar atualizada')
      setModal(false)
      setEditId(null)
      return
    }

    const nova: ContaPagar = {
      id: 'cp' + Date.now(),
      numero_titulo: gerarCodigo(),
      ...form,
      valor_pago: 0,
      status: 'PENDENTE',
      createdAt: new Date().toISOString(),
    }
    setContasPagar(prev => [...prev, nova])
    addLog('CONTA_PAGAR', nova.id, 'CRIADO', `Conta ${nova.numero_titulo} criada - ${formatCurrency(nova.valor_previsto)}`)
    showToast('Conta a pagar cadastrada')
    setModal(false)
  }

  function openNew() {
    setEditId(null)
    setForm({ ...emptyForm })
    setErrors({})
    setModal(true)
  }

  function openEdit(conta: ContaPagar) {
    setEditId(conta.id)
    setForm({
      ordem_compra_id: conta.ordem_compra_id || '',
      obra_id: conta.obra_id,
      centro_custo_id: conta.centro_custo_id,
      categoria_orcamentaria_id: conta.categoria_orcamentaria_id,
      fornecedor_id: conta.fornecedor_id,
      descricao: conta.descricao,
      data_emissao: conta.data_emissao,
      data_vencimento: conta.data_vencimento,
      valor_previsto: conta.valor_previsto,
      origem: conta.origem,
      observacoes: conta.observacoes || '',
    })
    setErrors({})
    setModal(true)
  }

  async function deleteConta(conta: ContaPagar) {
    const confirmed = window.confirm(`Excluir a conta ${conta.numero_titulo}? Pagamentos vinculados tambem serao removidos.`)
    if (!confirmed) return

    const pagamentosConta = pagamentos.filter(item => item.conta_pagar_id === conta.id)
    try {
      await persistDeletedVersions('pagamentos', pagamentosConta as unknown as Record<string, unknown>[])
      await persistDeletedVersion('contas_pagar', conta as unknown as Record<string, unknown>)
    } catch {
      showToast('Erro, tente novamente mais tarde.')
      return
    }

    if (conta.ordem_compra_id) {
      const totalPagoConta = pagamentosConta.reduce((sum, item) => sum + item.valor_pago, 0)
      const itensOC = itensOrdemCompra.filter(item => item.ordem_compra_id === conta.ordem_compra_id)
      const totalOC = itensOC.reduce((sum, item) => sum + item.valor_total, 0)

      if (totalPagoConta > 0 && totalOC > 0) {
        setItensOrcados(prev => prev.map(item => {
          const valorDoItem = itensOC
            .filter(itemOC => itemOC.item_orcado_id === item.id)
            .reduce((sum, itemOC) => sum + itemOC.valor_total, 0)

          if (!valorDoItem) return item

          const estorno = totalPagoConta * (valorDoItem / totalOC)
          const valorPago = Math.max(0, item.valor_pago - estorno)
          return {
            ...item,
            valor_pago: valorPago,
            saldo_atual: item.valor_total_previsto - item.valor_comprometido - valorPago,
          }
        }))
      }
    }

    setContasPagar(prev => prev.filter(item => item.id !== conta.id))
    setPagamentos(prev => prev.filter(item => item.conta_pagar_id !== conta.id))
    addLog('CONTA_PAGAR', conta.id, 'CANCELADO', `Conta ${conta.numero_titulo} excluida${conta.ordem_compra_id ? ' com estorno do valor pago no orcamento' : ''}`)
    showToast('Conta a pagar excluida')
  }

  function exportExcel() {
    const headers = ['Titulo', 'Descricao', 'Unidade', 'Obra', 'Fornecedor', 'Categoria', 'Vencimento', 'Valor previsto', 'Valor pago', 'Saldo', 'Status']
    const rows = filtered.map(conta => [
      conta.numero_titulo,
      conta.descricao,
      unidadeName(conta.obra_id),
      obraName(conta.obra_id),
      fornName(conta.fornecedor_id),
      categoriaName(conta.categoria_orcamentaria_id),
      formatDate(conta.data_vencimento),
      conta.valor_previsto.toFixed(2),
      conta.valor_pago.toFixed(2),
      saldoAberto(conta).toFixed(2),
      conta.status.replace(/_/g, ' '),
    ])
    const html = `
      <html>
        <head><meta charset="UTF-8" /></head>
        <body>
          <table border="1">
            <thead><tr>${headers.map(header => `<th>${escapeCell(header)}</th>`).join('')}</tr></thead>
            <tbody>${rows.map(row => `<tr>${row.map(cell => `<td>${escapeCell(cell)}</td>`).join('')}</tr>`).join('')}</tbody>
          </table>
        </body>
      </html>
    `
    const blob = new Blob([html], { type: 'application/vnd.ms-excel;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `financeiro-contas-${new Date().toISOString().slice(0, 10)}.xls`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  function clearFilters() {
    setFilterStatus('')
    setFilterUnidade('')
    setFilterObra('')
    setFilterFornecedor('')
    setFilterCategoria('')
    setPeriodoInicio('')
    setPeriodoFim('')
  }

  function inputStyle(error?: string) {
    return {
      width: '100%',
      background: '#1c1d24',
      border: `1px solid ${error ? '#f87171' : 'rgba(255,255,255,0.08)'}`,
      borderRadius: 6,
      padding: '7px 10px',
      color: '#f7f8f8',
      fontSize: 13,
      outline: 'none',
      boxSizing: 'border-box' as const,
    }
  }

  return (
    <div>
      {toast && <div style={{ position: 'fixed', top: 20, right: 24, background: '#22c55e', color: '#fff', padding: '10px 18px', borderRadius: 8, fontSize: 13, zIndex: 9999, fontWeight: 500 }}>{toast}</div>}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <h1 style={{ color: '#f7f8f8', fontSize: 18, fontWeight: 600, margin: 0 }}>Financeiro</h1>
          <p style={{ color: '#62666d', fontSize: 13, margin: '2px 0 0' }}>Dashboard de contas a pagar e pagas</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={exportExcel} style={{ background: '#1c1d24', color: '#d0d6e0', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 7, padding: '8px 16px', fontSize: 13, cursor: 'pointer', fontWeight: 500 }}>Exportar Excel</button>
          <button onClick={openNew} style={{ background: '#f0403b', color: '#fff', border: 'none', borderRadius: 7, padding: '8px 16px', fontSize: 13, cursor: 'pointer', fontWeight: 500 }}>+ Nova Conta</button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 16 }}>
        {[
          { label: 'Previsto', value: formatCurrency(totalPrevisto), color: '#f7f8f8', sub: `${filtered.length} titulos` },
          { label: 'A pagar', value: formatCurrency(totalAberto), color: '#fbbf24', sub: `${qtdAbertas} em aberto` },
          { label: 'Pago', value: formatCurrency(totalPago), color: '#4ade80', sub: `${qtdPagas} pagos` },
          { label: 'Vencido', value: formatCurrency(totalVencido), color: totalVencido > 0 ? '#f87171' : '#8a8f98', sub: 'saldo vencido' },
          { label: 'Saldo', value: formatCurrency(Math.max(0, totalPrevisto - totalPago)), color: '#60a5fa', sub: 'previsto - pago' },
        ].map(card => (
          <div key={card.label} className="card" style={{ padding: '14px 16px' }}>
            <p style={{ color: '#62666d', fontSize: 11, fontWeight: 500, marginBottom: 6, textTransform: 'uppercase' }}>{card.label}</p>
            <p style={{ color: card.color, fontSize: 18, fontWeight: 700 }}>{card.value}</p>
            <p style={{ color: '#62666d', fontSize: 11, marginTop: 4 }}>{card.sub}</p>
          </div>
        ))}
      </div>

      <div className="card" style={{ padding: 14, marginBottom: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr', gap: 10, alignItems: 'end' }}>
          <label>
            <span className="label-field">Unidade</span>
            <select className="input-field" value={filterUnidade} onChange={event => { setFilterUnidade(event.target.value); setFilterObra('') }}>
              <option value="">Todas</option>
              {unidades.map(unidade => <option key={unidade.id} value={unidade.id}>{unidade.nome}</option>)}
            </select>
          </label>
          <label>
            <span className="label-field">Obra</span>
            <select className="input-field" value={filterObra} onChange={event => setFilterObra(event.target.value)}>
              <option value="">Todas</option>
              {obrasFiltro.map(obra => <option key={obra.id} value={obra.id}>{obra.nome_obra}</option>)}
            </select>
          </label>
          <label>
            <span className="label-field">Status</span>
            <select className="input-field" value={filterStatus} onChange={event => setFilterStatus(event.target.value)}>
              <option value="">Todos</option>
              {Object.keys(STATUS_COLOR).map(status => <option key={status} value={status}>{status.replace(/_/g, ' ')}</option>)}
            </select>
          </label>
          <label>
            <span className="label-field">Fornecedor</span>
            <select className="input-field" value={filterFornecedor} onChange={event => setFilterFornecedor(event.target.value)}>
              <option value="">Todos</option>
              {fornecedores.map(fornecedor => <option key={fornecedor.id} value={fornecedor.id}>{fornecedor.nome_fantasia}</option>)}
            </select>
          </label>
          <label>
            <span className="label-field">Categoria</span>
            <select className="input-field" value={filterCategoria} onChange={event => setFilterCategoria(event.target.value)}>
              <option value="">Todas</option>
              {categorias.map(categoria => <option key={categoria.id} value={categoria.id}>{categoria.nome}</option>)}
            </select>
          </label>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '150px 150px auto', gap: 10, alignItems: 'end', marginTop: 10 }}>
          <label>
            <span className="label-field">Inicio</span>
            <input className="input-field" type="date" value={periodoInicio} onChange={event => setPeriodoInicio(event.target.value)} />
          </label>
          <label>
            <span className="label-field">Fim</span>
            <input className="input-field" type="date" value={periodoFim} onChange={event => setPeriodoFim(event.target.value)} />
          </label>
          <button onClick={clearFilters} className="btn-ghost" style={{ height: 35 }}>Limpar</button>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: '#62666d' }}>
          <p style={{ fontSize: 14 }}>Nenhuma conta encontrada para os filtros aplicados.</p>
        </div>
      ) : (
        <div className="card" style={{ overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
              {['Titulo', 'Descricao', 'Unidade', 'Obra', 'Fornecedor', 'Vencimento', 'Previsto', 'Pago', 'Saldo', 'Status', 'Acoes'].map(h => <th key={h} style={{ textAlign: 'left', padding: '8px 12px', color: '#62666d', fontWeight: 500 }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {paginatedContas.items.map(conta => {
                const saldo = saldoAberto(conta)
                const atrasada = conta.data_vencimento < hoje && !['PAGO', 'CANCELADO'].includes(conta.status)
                return (
                  <tr key={conta.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)', background: atrasada ? 'rgba(248,113,113,0.03)' : 'transparent' }} className="table-row">
                    <td style={{ padding: '10px 12px', color: '#ffb4b1', fontFamily: 'monospace', fontSize: 11 }}>{conta.numero_titulo}</td>
                    <td style={{ padding: '10px 12px', color: '#f7f8f8', fontWeight: 500 }}>{conta.descricao}</td>
                    <td style={{ padding: '10px 12px', color: '#a0a4ab' }}>{unidadeName(conta.obra_id)}</td>
                    <td style={{ padding: '10px 12px', color: '#a0a4ab' }}>{obraName(conta.obra_id)}</td>
                    <td style={{ padding: '10px 12px', color: '#a0a4ab' }}>{fornName(conta.fornecedor_id)}</td>
                    <td style={{ padding: '10px 12px', color: atrasada ? '#f87171' : '#a0a4ab', fontWeight: atrasada ? 600 : 400 }}>{formatDate(conta.data_vencimento)}</td>
                    <td style={{ padding: '10px 12px', color: '#a0a4ab' }}>{formatCurrency(conta.valor_previsto)}</td>
                    <td style={{ padding: '10px 12px', color: '#4ade80' }}>{formatCurrency(conta.valor_pago)}</td>
                    <td style={{ padding: '10px 12px', color: saldo > 0 ? '#fbbf24' : '#4ade80', fontWeight: 600 }}>{formatCurrency(saldo)}</td>
                    <td style={{ padding: '10px 12px' }}>
                      <span style={{ background: (STATUS_COLOR[conta.status] || '#94a3b8') + '22', color: STATUS_COLOR[conta.status] || '#94a3b8', borderRadius: 5, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>{conta.status.replace(/_/g, ' ')}</span>
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      <button onClick={() => openEdit(conta)} style={{ background: '#1c1d24', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 5, padding: '4px 10px', color: '#ffb4b1', fontSize: 12, cursor: 'pointer', marginRight: 6 }}>Editar</button>
                      <button onClick={() => deleteConta(conta)} style={{ background: '#1c1d24', border: '1px solid rgba(248,113,113,0.25)', borderRadius: 5, padding: '4px 10px', color: '#f87171', fontSize: 12, cursor: 'pointer' }}>Excluir</button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          <PaginationControls
            page={paginatedContas.safePage}
            pageSize={pageSize}
            totalItems={filtered.length}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
        </div>
      )}

      {modal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#16171e', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: 28, width: 540, maxHeight: '85vh', overflowY: 'auto' }}>
            <h2 style={{ color: '#f7f8f8', fontSize: 16, fontWeight: 600, margin: '0 0 20px' }}>{editId ? 'Editar Conta a Pagar' : 'Nova Conta a Pagar'}</h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div style={{ gridColumn: 'span 2' }}>
                <label style={{ color: '#a0a4ab', fontSize: 12, display: 'block', marginBottom: 4 }}>Descricao *</label>
                <input value={form.descricao} onChange={e => setForm(p => ({ ...p, descricao: e.target.value }))} style={inputStyle(errors.descricao)} />
                {errors.descricao && <p style={{ color: '#f87171', fontSize: 11, margin: '3px 0 0' }}>{errors.descricao}</p>}
              </div>
              <div>
                <label style={{ color: '#a0a4ab', fontSize: 12, display: 'block', marginBottom: 4 }}>Obra *</label>
                <select value={form.obra_id} onChange={e => setForm(p => ({ ...p, obra_id: e.target.value, centro_custo_id: '' }))} style={inputStyle(errors.obra_id)}>
                  <option value="">Selecione...</option>
                  {obras.map(o => <option key={o.id} value={o.id}>{o.nome_obra}</option>)}
                </select>
                {errors.obra_id && <p style={{ color: '#f87171', fontSize: 11, margin: '3px 0 0' }}>{errors.obra_id}</p>}
              </div>
              <div>
                <label style={{ color: '#a0a4ab', fontSize: 12, display: 'block', marginBottom: 4 }}>Centro de Custo</label>
                <select value={form.centro_custo_id} onChange={e => setForm(p => ({ ...p, centro_custo_id: e.target.value }))} disabled={!form.obra_id} style={inputStyle()}>
                  <option value="">Selecione...</option>
                  {ccsFiltrados.map(cc => <option key={cc.id} value={cc.id}>{cc.nome}</option>)}
                </select>
              </div>
              <div style={{ gridColumn: 'span 2' }}>
                <label style={{ color: '#a0a4ab', fontSize: 12, display: 'block', marginBottom: 4 }}>Fornecedor *</label>
                <select value={form.fornecedor_id} onChange={e => setForm(p => ({ ...p, fornecedor_id: e.target.value }))} style={inputStyle(errors.fornecedor_id)}>
                  <option value="">Selecione...</option>
                  {fornecedores.filter(f => f.status === 'ATIVO').map(f => <option key={f.id} value={f.id}>{f.nome_fantasia}</option>)}
                </select>
                {errors.fornecedor_id && <p style={{ color: '#f87171', fontSize: 11, margin: '3px 0 0' }}>{errors.fornecedor_id}</p>}
              </div>
              <div>
                <label style={{ color: '#a0a4ab', fontSize: 12, display: 'block', marginBottom: 4 }}>Categoria</label>
                <select value={form.categoria_orcamentaria_id} onChange={e => setForm(p => ({ ...p, categoria_orcamentaria_id: e.target.value }))} style={inputStyle()}>
                  <option value="">Selecione...</option>
                  {categorias.map(categoria => <option key={categoria.id} value={categoria.id}>{categoria.nome}</option>)}
                </select>
              </div>
              <div>
                <label style={{ color: '#a0a4ab', fontSize: 12, display: 'block', marginBottom: 4 }}>Origem</label>
                <select value={form.origem} onChange={e => setForm(p => ({ ...p, origem: e.target.value as any }))} style={inputStyle()}>
                  <option value="LANCAMENTO_MANUAL_CONTROLADO">MANUAL CONTROLADO</option>
                  <option value="ORDEM_COMPRA">ORDEM DE COMPRA</option>
                  <option value="MAO_DE_OBRA">MAO DE OBRA</option>
                </select>
              </div>
              <div>
                <label style={{ color: '#a0a4ab', fontSize: 12, display: 'block', marginBottom: 4 }}>Valor Previsto *</label>
                <input type="number" value={form.valor_previsto} onChange={e => setForm(p => ({ ...p, valor_previsto: parseFloat(e.target.value) || 0 }))} style={inputStyle(errors.valor_previsto)} />
                {errors.valor_previsto && <p style={{ color: '#f87171', fontSize: 11, margin: '3px 0 0' }}>{errors.valor_previsto}</p>}
              </div>
              <div>
                <label style={{ color: '#a0a4ab', fontSize: 12, display: 'block', marginBottom: 4 }}>Vencimento *</label>
                <input type="date" value={form.data_vencimento} onChange={e => setForm(p => ({ ...p, data_vencimento: e.target.value }))} style={inputStyle(errors.data_vencimento)} />
                {errors.data_vencimento && <p style={{ color: '#f87171', fontSize: 11, margin: '3px 0 0' }}>{errors.data_vencimento}</p>}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 24 }}>
              <button onClick={() => setModal(false)} style={{ background: '#1c1d24', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 7, padding: '8px 18px', color: '#a0a4ab', fontSize: 13, cursor: 'pointer' }}>Cancelar</button>
              <button onClick={save} style={{ background: '#f0403b', border: 'none', borderRadius: 7, padding: '8px 18px', color: '#fff', fontSize: 13, cursor: 'pointer', fontWeight: 500 }}>{editId ? 'Salvar alteracoes' : 'Cadastrar Conta'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

